import { supabase } from './supabase';
import { NavigateFunction } from 'react-router-dom';

export async function waitForSessionUpdate(
  sessionId: string,
  fieldToCheck: string,
  maxAttempts: number = 20,
  intervalMs: number = 500,
  oldValue?: string
): Promise<boolean> {
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    const { data, error } = await supabase
      .from('sessions')
      .select(fieldToCheck)
      .eq('id', sessionId)
      .maybeSingle();

    if (error) {
      console.error(`Error checking ${fieldToCheck}:`, error);
      await new Promise(resolve => setTimeout(resolve, intervalMs));
      continue;
    }

    const fieldValue = data?.[fieldToCheck as keyof typeof data];

    if (fieldValue !== null && fieldValue !== undefined) {
      if (oldValue !== undefined) {
        if (typeof fieldValue === 'string' && fieldValue !== oldValue && fieldValue.length > 0) {
          console.log(`✓ ${fieldToCheck} changed in database (from ${oldValue.length} to ${fieldValue.length} chars)`);
          return true;
        }
      } else {
        if (Array.isArray(fieldValue) && fieldValue.length > 0) {
          console.log(`✓ ${fieldToCheck} verified in database (array with ${fieldValue.length} items)`);
          return true;
        } else if (typeof fieldValue === 'string' && fieldValue.length > 0) {
          console.log(`✓ ${fieldToCheck} verified in database (string with ${fieldValue.length} chars)`);
          return true;
        } else if (typeof fieldValue === 'object' && fieldValue !== null) {
          console.log(`✓ ${fieldToCheck} verified in database (object):`, fieldValue);
          return true;
        }
      }
    }

    console.log(`Field ${fieldToCheck} value:`, fieldValue, 'Type:', typeof fieldValue);

    console.log(`Waiting for ${fieldToCheck}... (attempt ${attempt + 1}/${maxAttempts})`);
    await new Promise(resolve => setTimeout(resolve, intervalMs));
  }

  console.error(`Failed to verify ${fieldToCheck} after ${maxAttempts} attempts`);
  return false;
}

export async function saveAndNavigate(
  sessionId: string,
  updates: Record<string, any>,
  fieldsToVerify: string[],
  navigate: NavigateFunction,
  targetRoute: string,
  navigationState?: any,
  maxRetries: number = 3
): Promise<boolean> {
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      console.log(`Saving to database (attempt ${attempt + 1}/${maxRetries})...`, updates);

      const { data, error } = await supabase
        .from('sessions')
        .update(updates)
        .eq('id', sessionId)
        .select('*')
        .single();

      if (error) {
        console.error('Database save error:', error);
        if (attempt < maxRetries - 1) {
          await new Promise(resolve => setTimeout(resolve, 1000));
          continue;
        }
        throw error;
      }

      console.log('Database save successful, verifying...');

      let allVerified = true;
      for (const field of fieldsToVerify) {
        const verified = await waitForSessionUpdate(sessionId, field, 10, 500);
        if (!verified) {
          allVerified = false;
          console.error(`Failed to verify field: ${field}`);
        }
      }

      if (!allVerified) {
        if (attempt < maxRetries - 1) {
          console.log('Verification failed, retrying...');
          await new Promise(resolve => setTimeout(resolve, 1000));
          continue;
        }
        throw new Error('Failed to verify database updates');
      }

      console.log('All fields verified, navigating to:', targetRoute);
      navigate(targetRoute, navigationState ? { state: navigationState } : undefined);
      return true;
    } catch (err) {
      console.error(`Save and navigate attempt ${attempt + 1} failed:`, err);
      if (attempt === maxRetries - 1) {
        throw err;
      }
    }
  }

  return false;
}

export async function verifySessionData(
  sessionId: string,
  requiredFields: string[]
): Promise<{ success: boolean; missingFields: string[] }> {
  try {
    const { data, error } = await supabase
      .from('sessions')
      .select(requiredFields.join(','))
      .eq('id', sessionId)
      .maybeSingle();

    if (error) {
      console.error('Error verifying session data:', error);
      return { success: false, missingFields: requiredFields };
    }

    const missingFields: string[] = [];
    for (const field of requiredFields) {
      const value = data?.[field as keyof typeof data];
      if (value === null || value === undefined || value === '') {
        missingFields.push(field);
      } else if (Array.isArray(value) && value.length === 0) {
        missingFields.push(field);
      }
    }

    return {
      success: missingFields.length === 0,
      missingFields,
    };
  } catch (err) {
    console.error('Exception verifying session data:', err);
    return { success: false, missingFields: requiredFields };
  }
}
